import { ChainType } from "@0xsquid/squid-types";
import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { useCosmosContext } from "../../core";
import { keys } from "../../core/queries/queries-keys";
import { SwapDirection } from "../../core/types/swap";
import { TokenWithBalance } from "../../core/types/tokens";
import {
  calculateTotalUsdBalanceUSD,
  getAllKeysForSupportedCosmosChains,
} from "../../services";
import {
  getAllBitcoinTokensBalance,
  getAllCantonTokensBalance,
  getAllCosmosBalances,
  getAllEvmTokensBalance,
  getAllSolanaTokensBalance,
  getAllStellarTokensBalance,
  getAllSuiTokensBalance,
  getAllXrplTokensBalance,
} from "../../services/external/rpcService";
import { useSquidChains } from "../chains/useSquidChains";
import { useWalletStore } from "../store/useWalletStore";
import { useSquidTokens } from "./useSquidTokens";

export interface AllTokensBalanceQueryData {
  tokens: TokenWithBalance[];
  totalUsdBalance: string;
}

export const useAllTokensWithBalanceForChainType = ({
  chainType,
  address,
  direction,
  queryOptions,
}: {
  chainType?: ChainType;
  address?: string;
  direction?: SwapDirection;
  queryOptions?: UseQueryOptions<AllTokensBalanceQueryData>;
}) => {
  const { evmChains, cosmosChains, suiChains, xrplChains, stellarChains } =
    useSquidChains(direction);
  const {
    evmTokens,
    cosmosTokens,
    solanaTokens,
    bitcoinTokens,
    suiTokens,
    xrplTokens,
    stellarTokens,
    cantonTokens,
  } = useSquidTokens(direction);
  const { keplrTypeWallet } = useCosmosContext();
  const cantonConnector = useWalletStore(
    (store) =>
      store.connectedWalletsByChainType[ChainType.CANTON]?.wallet?.connector
  );

  const placeholderData = useMemo<AllTokensBalanceQueryData>(() => {
    const tokens: Record<ChainType, TokenWithBalance[]> = {
      [ChainType.EVM]: evmTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.COSMOS]: cosmosTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.SOLANA]: solanaTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.BTC]: bitcoinTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.SUI]: suiTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.XRPL]: xrplTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.STELLAR]: stellarTokens.map((t) => ({ ...t, balance: "0" })),
      [ChainType.CANTON]: cantonTokens.map((t) => ({ ...t, balance: "0" })),
    };

    if (!chainType) {
      // Return all tokens with zero balance
      return {
        tokens: Object.values(tokens).flat(),
        totalUsdBalance: "0",
      };
    }
    return {
      tokens: tokens[chainType],
      totalUsdBalance: "0",
    };
  }, [
    chainType,
    evmTokens,
    cosmosTokens,
    solanaTokens,
    bitcoinTokens,
    suiTokens,
    xrplTokens,
    stellarTokens,
    cantonTokens,
  ]);

  const isQueryEnabled = useMemo((): boolean => {
    // Respect the queryOptions.enabled override if provided
    if (queryOptions?.enabled === false) return false;
    if (!chainType || !address) return false;

    switch (chainType) {
      case ChainType.EVM:
        return evmTokens.length > 0;

      case ChainType.COSMOS:
        return cosmosTokens.length > 0;

      case ChainType.SOLANA:
        return solanaTokens.length > 0;

      case ChainType.BTC:
        return bitcoinTokens.length > 0;

      case ChainType.SUI:
        return suiTokens.length > 0;

      case ChainType.XRPL:
        return xrplTokens.length > 0;

      case ChainType.STELLAR:
        return stellarTokens.length > 0;

      case ChainType.CANTON:
        return cantonTokens.length > 0;
    }
  }, [
    chainType,
    address,
    queryOptions?.enabled,
    evmTokens.length,
    cosmosTokens.length,
    solanaTokens.length,
    bitcoinTokens.length,
    suiTokens.length,
    xrplTokens.length,
    stellarTokens.length,
    cantonTokens.length,
  ]);

  const query = useQuery<AllTokensBalanceQueryData>(
    keys().allTokensBalance(address, chainType, direction),
    async () => {
      // Return zero balances if no address
      if (!address) {
        const defaultTokens = placeholderData.tokens;
        return {
          tokens: defaultTokens,
          totalUsdBalance: calculateTotalUsdBalanceUSD(defaultTokens),
        };
      }

      let fetchedTokens: TokenWithBalance[] = [];

      // Fetch tokens based on chain type
      switch (chainType) {
        case ChainType.EVM:
          fetchedTokens = await getAllEvmTokensBalance(
            evmTokens,
            address,
            evmChains
          );
          break;

        case ChainType.COSMOS:
          const addresses = await getAllKeysForSupportedCosmosChains(
            cosmosChains.map((c) => c.chainId),
            keplrTypeWallet
          );
          fetchedTokens = await getAllCosmosBalances({
            addresses,
            cosmosChains,
            cosmosTokens,
          });
          break;

        case ChainType.SOLANA:
          fetchedTokens = await getAllSolanaTokensBalance(
            solanaTokens,
            address
          );
          break;

        case ChainType.BTC:
          fetchedTokens = await getAllBitcoinTokensBalance(
            address,
            bitcoinTokens
          );
          break;

        case ChainType.SUI:
          fetchedTokens = await getAllSuiTokensBalance(
            address,
            suiTokens,
            suiChains
          );
          break;

        case ChainType.XRPL:
          fetchedTokens = await getAllXrplTokensBalance(
            address,
            xrplTokens,
            xrplChains
          );
          break;

        case ChainType.STELLAR:
          fetchedTokens = await getAllStellarTokensBalance(
            address,
            stellarTokens,
            stellarChains
          );
          break;

        case ChainType.CANTON:
          fetchedTokens = cantonConnector
            ? await getAllCantonTokensBalance({
                connector: cantonConnector,
                partyId: address,
                tokens: cantonTokens,
              })
            : placeholderData.tokens;
          break;

        default:
          fetchedTokens = placeholderData.tokens;
          break;
      }

      return {
        tokens: fetchedTokens,
        totalUsdBalance: calculateTotalUsdBalanceUSD(fetchedTokens),
      };
    },
    {
      ...queryOptions,
      enabled: isQueryEnabled,
    }
  );

  // Return placeholder if not fetched yet, otherwise return query data
  return {
    ...query,
    data: query.data ?? placeholderData,
  } as UseQueryResult<AllTokensBalanceQueryData>;
};
